import type { ReactNode } from "react";

import {
  IconMenuButton,
  IconMenuButtonContent,
  PressableMenuItem,
} from "@/components/ui/icon-menu-button";

// The donor's one-item edit menu — `components/edit-menu/edit-menu.tsx`,
// shared in the source (the spec sheets' notes rows open it), so shared here
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"; #808).
//
// A MUI `Menu` — so a modal, on the shared icon-menu layer rather than a third
// one — anchored `top/left` against a `top/right` transform: the paper's
// top-right corner on the trigger's top-left, opening leftward over the ⋮
// (`side="left" align="start"`, #798's kebab precedent). `minWidth: 104px` on
// the paper. One row, `data-testid='edit-menu-item'`, reading the donor's
// `label` (`Edit` by default), which may be held disabled.
//
// The trigger is the caller's, as the donor's `anchorEl` is: an
// `IconMenuButtonTrigger` in the box and colour its screen draws.
//
// **The row's click stops there.** The donor's caller stops it in `handleEdit`
// (`expandable-notes-row.tsx:66-70`) — a React click bubbles out of the menu's
// portal to the row that owns it, and would otherwise reach that row's own
// click (the Editor's Active selection).

/** `slotProps.paper.style.minWidth` (`edit-menu.tsx:33-38`). */
const EDIT_MENU_MIN_WIDTH_PX = 104;

export interface EditMenuProps {
  /** The ⋮ the menu hangs from — an `IconMenuButtonTrigger`. */
  trigger: ReactNode;
  onEdit: () => void;
  /** The row's text — the donor's `label`, `Edit` when omitted. */
  label?: string;
  disabled?: boolean;
}

export function EditMenu({ trigger, onEdit, label = "Edit", disabled = false }: EditMenuProps) {
  return (
    <IconMenuButton>
      {trigger}
      <IconMenuButtonContent
        data-testid="edit-menu-testid"
        minWidthPx={EDIT_MENU_MIN_WIDTH_PX}
        side="left"
        align="start"
        rowVariant="body1"
      >
        <PressableMenuItem
          data-testid="edit-menu-item"
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          onSelect={onEdit}
        >
          {label}
        </PressableMenuItem>
      </IconMenuButtonContent>
    </IconMenuButton>
  );
}
